/** A CSS easing that plays a spring, plus how long the spring takes to settle. */
export interface SpringEasing {
  /** A CSS `linear()` easing function sampled from the spring. */
  easing: string
  durationMs: number
}

/** The spring counts as settled once it stays this close to its target. */
const SETTLED = 0.001

/**
 * Turn a damped spring (mass 1) into a CSS `linear()` easing and a duration,
 * so a plain CSS transition can bounce. A damping ratio below 1 overshoots the
 * target and settles back: Settle, the top row's return (PROD-1524), dips a
 * few pixels past its spot once.
 */
export function springEasing({
  stiffness,
  damping,
  samples = 40,
}: {
  stiffness: number
  damping: number
  samples?: number
}): SpringEasing {
  const w0 = Math.sqrt(stiffness)
  const zeta = damping / (2 * w0)
  const wd = w0 * Math.sqrt(Math.max(1e-9, 1 - zeta * zeta))
  const at = (t: number): number => {
    const envelope = Math.exp(-zeta * w0 * t)
    return zeta < 1
      ? 1 - envelope * (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t))
      : 1 - envelope * (1 + w0 * t)
  }
  let settledAt = 0
  for (let t = 0; t < 3; t += 1 / 240) {
    if (Math.abs(1 - at(t)) > SETTLED) {
      settledAt = t
    }
  }
  const points = Array.from({ length: samples + 1 }, (_, i) =>
    i === samples ? 1 : Math.round(at((settledAt * i) / samples) * 10000) / 10000,
  )
  return { easing: `linear(${points.join(', ')})`, durationMs: Math.round(settledAt * 1000) }
}
